import { Controller } from "@hotwired/stimulus"

// Accessibility-focused navigation controller
// - opens/closes the slideout
// - traps focus while open
// - closes on Escape or click outside
export default class extends Controller {
  static targets = ["toggle", "menu", "overlay"]

  connect() {
    // ensure controllers are correctly attached
    if (this.hasToggleTarget) this.toggleTarget.setAttribute('aria-expanded', 'false')
    if (this.hasMenuTarget) this.menuTarget.setAttribute('aria-hidden', 'true')
    if (this.hasOverlayTarget) this.overlayTarget.setAttribute('aria-hidden', 'true')

    this._previouslyFocused = null
    this._boundKeydown = this._handleKeydown.bind(this)
    this._boundDelegateClick = this._handleDelegateClick.bind(this)
    // delegate clicks within the controller scope to handle smooth scrolls
    this.element.addEventListener('click', this._boundDelegateClick)
  }

  disconnect() {
    // clean up listeners
    document.removeEventListener('keydown', this._boundKeydown)
    this.element.removeEventListener('click', this._boundDelegateClick)
  }

  toggle(event) {
    event && event.preventDefault()
    if (!this.hasMenuTarget) return
    if (this.menuTarget.classList.contains('is-open')) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (!this.hasMenuTarget) return

    this._previouslyFocused = document.activeElement
    this.menuTarget.classList.add('is-open')
    this.menuTarget.setAttribute('aria-hidden', 'false')
  this.menuTarget.setAttribute('aria-modal', 'true')
    if (this.hasToggleTarget) this.toggleTarget.setAttribute('aria-expanded', 'true')
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add('is-open')
      this.overlayTarget.setAttribute('aria-hidden', 'false')
    }

    this._focusable = this._getFocusableElements()
    this._firstFocusable = this._focusable[0]
    this._lastFocusable = this._focusable[this._focusable.length - 1]

    // focus first meaningful element in the menu (close button first)
    if (this._firstFocusable) this._firstFocusable.focus()

    // delegated click handler on the controller element handles link/button actions
    document.addEventListener('keydown', this._boundKeydown)
  }

  close() {
    if (!this.hasMenuTarget) return

    this.menuTarget.classList.remove('is-open')
    this.menuTarget.setAttribute('aria-hidden', 'true')
    if (this.hasToggleTarget) this.toggleTarget.setAttribute('aria-expanded', 'false')
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove('is-open')
      this.overlayTarget.setAttribute('aria-hidden', 'true')
    }
    this.menuTarget.setAttribute('aria-modal', 'false')

  // remove listeners
  document.removeEventListener('keydown', this._boundKeydown)

    // restore focus
    try {
      if (this._previouslyFocused && this._previouslyFocused.focus) this._previouslyFocused.focus()
    } catch (e) {
      // ignore
    }
  }

  _handleMenuClick(event) {
    // kept for backward compatibility; delegate handler covers this
    const link = event.target.closest('a')
    const button = event.target.closest('button')
    if (link || button) setTimeout(() => this.close(), 0)
  }

  _handleDelegateClick(event) {
    // handle anchor clicks for same-page smooth scrolling and close menu when appropriate
    const anchor = event.target.closest('a')
    const button = event.target.closest('button')

    if (!anchor && !button) return

    // If anchor is present, consider smooth-scrolling for same-page hashes
    if (anchor) {
      const href = anchor.getAttribute('href')
      if (!href) return

      let url
      try {
        url = new URL(href, window.location.href)
      } catch (e) {
        return
      }

      const isSameOrigin = url.origin === window.location.origin
      const isSamePath = url.pathname === window.location.pathname
      const hasHash = Boolean(url.hash)

      if (hasHash && isSameOrigin && isSamePath) {
        // smooth-scroll to target id
        event.preventDefault()
        const targetId = decodeURIComponent(url.hash.slice(1))
        const doScroll = () => {
            const el = document.getElementById(targetId) || document.querySelector(`[name="${targetId}"]`)
            const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
            const behavior = prefersReduced ? 'auto' : 'smooth'
            const headerOffset = 60 // pixels to offset for sticky header
            if (el) {
              const rect = el.getBoundingClientRect()
              const absoluteY = window.pageYOffset + rect.top
              const scrollY = Math.max(0, absoluteY - headerOffset)
              window.scrollTo({ top: scrollY, behavior })
            } else {
              window.scrollTo({ top: 0, behavior })
            }
          }

          // close the menu if open, then scroll. If the user prefers reduced
          // motion, skip the close-animation delay so scrolling is immediate.
          const delay = (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ? 0 : 300
          if (this.hasMenuTarget && this.menuTarget.classList.contains('is-open')) {
            this.close()
            setTimeout(doScroll, delay)
          } else {
            doScroll()
          }
        return
      }
    }

    // If a button was clicked within the menu (non-anchor), close the menu
    if (button && this.hasMenuTarget && this.menuTarget.contains(button)) {
      setTimeout(() => this.close(), 0)
    }
  }

  // Private methods
  _handleKeydown(event) {
    // close on Escape
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault()
      this.close()
      return
    }

    // trap Tab navigation within the slideout
    if (event.key === 'Tab') {
      this._focusable = this._getFocusableElements()
      if (this._focusable.length === 0) {
        event.preventDefault()
        return
      }

      const first = this._focusable[0]
      const last = this._focusable[this._focusable.length - 1]

      if (event.shiftKey) {
        // shift+tab
        if (document.activeElement === first) {
          event.preventDefault()
          last.focus()
        }
      } else {
        // tab
        if (document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
  }

  _getFocusableElements() {
    if (!this.hasMenuTarget) return []
    const selector = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]'
    return Array.from(this.menuTarget.querySelectorAll(selector)).filter(el => el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length)
  }
}
